import { ApiResponse, axiosInstance } from '@/lib/axios';
import { FetchParams, IPagination } from '@/types/general.type';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/lib/react-query';
import { TChannel } from '../../../_shared/types/channel.type';

export type TGetListIntegrationLineResponse = ApiResponse<IPagination<TChannel>>;
export type TGetListIntegrationLineParams = FetchParams;

export const getListIntegrationLine = async (
  params: TGetListIntegrationLineParams
): Promise<TGetListIntegrationLineResponse> => {
  const url = '/integration/line/org-channel';

  return axiosInstance.get(url, { params });
};

export const GET_LIST_INTEGRATION_LINE_QUERY_KEY = 'get-list-integration-line';

export const getListIntegrationLineQueryOptions = <TData = IPagination<TChannel>>(
  params: TGetListIntegrationLineParams,
  select?: (data: TGetListIntegrationLineResponse) => TData
) => {
  return queryOptions({
    queryKey: [
      GET_LIST_INTEGRATION_LINE_QUERY_KEY,
      {
        page: params?.page,
        limit: params?.limit,
        search: params?.search,
        sortDir: params?.sortDir,
      },
    ],
    queryFn: () => getListIntegrationLine(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useGetListIntegrationLineQuery<TData>(options: {
  params: TGetListIntegrationLineParams;
  queryConfig?: QueryConfig<typeof getListIntegrationLineQueryOptions<TData>>;
  select: (data: TGetListIntegrationLineResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useGetListIntegrationLineQuery(options: {
  params: TGetListIntegrationLineParams;
  queryConfig?: QueryConfig<typeof getListIntegrationLineQueryOptions<TGetListIntegrationLineResponse>>;
}): ReturnType<typeof useQuery<TGetListIntegrationLineResponse>>;

// Implementation
export function useGetListIntegrationLineQuery<TData = TGetListIntegrationLineResponse>({
  queryConfig,
  params,
  select,
}: {
  params: TGetListIntegrationLineParams;
  queryConfig?: QueryConfig<typeof getListIntegrationLineQueryOptions<TData | TGetListIntegrationLineResponse>>;
  select?: (data: TGetListIntegrationLineResponse) => TData;
}) {
  return useQuery({
    ...getListIntegrationLineQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<
    typeof useQuery<TData extends TGetListIntegrationLineResponse ? TGetListIntegrationLineResponse : TData>
  >;
}
